import React, { useState, useEffect } from "react";
import { Clock, AlertTriangle, Calendar, CheckCircle, XCircle, Users } from "lucide-react";

const OneTimeTaskCard = React.memo(({ task }) => {
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [isMobile, setIsMobile] = useState(false);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(timeInterval);
  }, []);

  // Early return if task is undefined (after all hooks)
  if (!task) {
    return null;
  }

  // Extract task properties
  const taskName = task.taskName || task.taskId?.taskName || '';
  const description = task.description || task.taskId?.description || '';
  const priority = task.priority || task.taskId?.priority || '';
  const startDateTime = task.startDateTime || 0;
  const endDateTime = task.endDateTime || 0;
  const status = task.status || 'pending';
  const assignedTo = task.assignedTo || [];

  const formatDateTime = (timestamp) => {
    return new Date(timestamp).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTaskStatus = () => {
    const now = currentTime;
    if (status === 'completed') return 'completed';
    if (now < startDateTime) return 'pending';
    if (now >= startDateTime && now <= endDateTime) return 'active';
    return 'missed';
  };

  const getRemainingTime = () => {
    const remaining = endDateTime - currentTime;
    if (remaining <= 0) return "Time's up!";

    const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
    const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days}d ${hours}h ${minutes}m remaining`;
    if (hours > 0) return `${hours}h ${minutes}m remaining`;
    return `${minutes}m remaining`;
  };

  const getTimeUntilStart = () => {
    const timeUntilStart = startDateTime - currentTime;
    if (timeUntilStart <= 0) return "Starting now!";

    const days = Math.floor(timeUntilStart / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeUntilStart % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeUntilStart % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `Starts in ${days}d ${hours}h`;
    if (hours > 0) return `Starts in ${hours}h ${minutes}m`;
    return `Starts in ${minutes}m`;
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'bg-red-100 border-red-300 text-red-800';
      case 'medium':
        return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      case 'low':
        return 'bg-green-100 border-green-300 text-green-800';
      default:
        return 'bg-gray-100 border-gray-300 text-gray-800';
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return (
          <div className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <AlertTriangle className="w-3 h-3" />
            {isMobile ? 'High' : 'High Priority'}
          </div>
        );
      case 'medium':
        return (
          <div className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
            <Clock className="w-3 h-3" />
            {isMobile ? 'Med' : 'Medium'}
          </div>
        );
      case 'low':
        return (
          <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle className="w-3 h-3" />
            Low
          </div>
        );
      default:
        return null;
    }
  };

  const taskStatus = getTaskStatus();
  const isActive = taskStatus === 'active';
  const isCompleted = taskStatus === 'completed';
  const isPending = taskStatus === 'pending';
  const isMissed = taskStatus === 'missed';

  return (
    <div className={`group relative overflow-hidden rounded-xl shadow-md transition-all duration-300 hover:shadow-xl border-2 ${
      isActive ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200' :
      isCompleted ? 'bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200' :
      isMissed ? 'bg-gradient-to-br from-red-50 to-rose-50 border-red-200' :
      'bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200'
    } ${isMobile ? 'p-3 mb-3' : 'p-4 mb-4'}`}>

      {/* Active indicator */}
      {isActive && (
        <div className="absolute top-0 right-0 w-0 h-0 border-l-[40px] border-l-transparent border-t-[40px] border-t-blue-500">
          <div className="absolute -top-8 -right-2 text-white text-xs font-bold rotate-45">
            LIVE
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className={`font-bold text-gray-900 mb-2 ${isMobile ? 'text-base leading-tight' : 'text-lg'}`}>
            {taskName}
          </h3>
          <p className={`text-gray-600 mb-3 ${isMobile ? 'text-xs' : 'text-sm'}`}>
            {isMobile && description?.length > 80 ? `${description.substring(0, 80)}...` : description}
          </p>

          {/* Meta Information */}
          <div className={`flex flex-wrap items-center gap-2 ${isMobile ? 'mb-2' : 'mb-3'}`}>
            {getPriorityBadge(priority)}

            <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
              <Calendar className="w-3 h-3" />
              One Time
            </div>

            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              isCompleted ? 'bg-emerald-100 text-emerald-700' :
              isActive ? 'bg-blue-100 text-blue-700' :
              isMissed ? 'bg-red-100 text-red-700' :
              'bg-amber-100 text-amber-700'
            }`}>
              {isCompleted ? <CheckCircle className="w-3 h-3" /> :
               isActive ? <Clock className="w-3 h-3" /> :
               isMissed ? <XCircle className="w-3 h-3" /> :
               <Clock className="w-3 h-3" />}
              {isCompleted ? 'Completed' : isActive ? 'Active' : isMissed ? 'Missed' : 'Pending'}
            </div>

            {assignedTo?.length > 0 && (
              <div className="flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                <Users className="w-3 h-3" />
                {assignedTo.length} {assignedTo.length === 1 ? 'Person' : 'People'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Time Information */}
      <div className={`bg-white/50 rounded-lg p-3 mb-3 ${isMobile ? 'text-xs' : 'text-sm'}`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="flex items-start gap-2">
            <Clock className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-gray-700">Start Time</div>
              <div className="text-gray-600">{formatDateTime(startDateTime)}</div>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Clock className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-gray-700">End Time</div>
              <div className="text-gray-600">{formatDateTime(endDateTime)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Information */}
      <div className={`${isMobile ? 'text-xs' : 'text-sm'}`}>
        {isActive && (
          <div className="flex items-center gap-2 text-blue-600 font-semibold bg-blue-100 rounded-lg p-2 animate-pulse">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-ping"></div>
            <span>{getRemainingTime()}</span>
          </div>
        )}

        {isPending && (
          <div className="flex items-center gap-2 text-amber-600 font-medium bg-amber-100 rounded-lg p-2">
            <Clock className="w-4 h-4" />
            <span>{getTimeUntilStart()}</span>
          </div>
        )}

        {isCompleted && (
          <div className="flex items-center gap-2 text-emerald-600 font-semibold bg-emerald-100 rounded-lg p-2">
            <CheckCircle className="w-4 h-4" />
            <span>Task Completed Successfully</span>
          </div>
        )}

        {isMissed && (
          <div className="flex items-center gap-2 text-red-600 font-semibold bg-red-100 rounded-lg p-2">
            <XCircle className="w-4 h-4" />
            <span>Task Missed</span>
          </div>
        )}
      </div>
    </div>
  );
});

OneTimeTaskCard.displayName = 'OneTimeTaskCard';

export default OneTimeTaskCard;
